<template>
  <figure class="travel-details text-base">
    <table class="is-fullwidth mb-0 table">
      <thead>
        <tr>
          <th
            v-if="distanceMainStation || distanceAirport"
            colspan="2"
            class="!text-right"
          >
            {{ $texts('distance') }}
          </th>
          <th
            v-if="hasTravelTime"
            class="!h-full !border-b !pb-[8.5px] !pr-0 text-right tablet:!pb-[9px] desktop:!pb-[13px]"
          >
            <div class="flex items-baseline justify-between">
              <span>{{ $texts('travelTime') }}</span>
              <SpriteSymbol name="tram" class="size-4" />
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="distanceMainStation">
          <th>{{ $texts('zurichMainStation') }}</th>
          <td class="text-right">
            {{ distanceMainStation }}
          </td>
          <td v-if="hasTravelTime" class="text-right">
            <span v-if="travelTimeMainstation"
              >{{ travelTimeMainstation }} min</span
            >
          </td>
        </tr>
        <tr v-if="distanceAirport">
          <th>{{ $texts('zurichAirport') }}</th>
          <td class="text-right">
            {{ distanceAirport }}
          </td>
          <td v-if="hasTravelTime" class="text-right">
            <span v-if="travelTimeAirport">{{ travelTimeAirport }} min</span>
          </td>
        </tr>
      </tbody>
    </table>
  </figure>
</template>

<script lang="ts" setup>
import type { AddressTravelDetailsFragment } from '#graphql-operations'

interface GeoLocation {
  lat: number
  lon: number
}

const CommonLocations = {
  ZurichMainStation: { lat: 47.377778, lon: 8.540278 },
  ZurichAirport: { lat: 47.451583, lon: 8.565233 },
} as const

function calculateDistance(from: GeoLocation, to: GeoLocation) {
  let lat1 = from.lat
  let lon1 = from.lon
  let lat2 = to.lat
  let lon2 = to.lon

  // Mean radius of earth in kilometers.
  const r = 6372.797
  const pi80 = Math.PI / 180

  lat1 *= pi80
  lon1 *= pi80
  lat2 *= pi80
  lon2 *= pi80

  const dlat = lat2 - lat1
  const dlon = lon2 - lon1

  const a =
    Math.sin(dlat / 2) * Math.sin(dlat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dlon / 2) * Math.sin(dlon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return r * c
}

function formatDistance(distance: number) {
  if (distance < 1) {
    return Math.round(distance * 1000) + ' m'
  }
  return Math.round(distance * 10) / 10 + ' km'
}

const props = withDefaults(
  defineProps<{
    travelTimeAirport?: number
    travelTimeMainstation?: number
    location?: AddressTravelDetailsFragment['location'] | null
  }>(),
  {
    travelTimeAirport: undefined,
    travelTimeMainstation: undefined,
    location: undefined,
  },
)

const { $texts } = useEasyTexts()

const hasTravelTime = computed(
  () => props.travelTimeAirport || props.travelTimeMainstation,
)

const distanceMainStation = computed(() => {
  if (!props.location) {
    return
  }
  return formatDistance(
    calculateDistance(
      props.location as GeoLocation,
      CommonLocations.ZurichMainStation,
    ),
  )
})

const distanceAirport = computed(() => {
  if (!props.location) {
    return
  }
  return formatDistance(
    calculateDistance(
      props.location as GeoLocation,
      CommonLocations.ZurichAirport,
    ),
  )
})
</script>
